import styled from 'styled-components';

import { Box } from '../box';
import { TanstackTableProps } from './table.types';

export const TableWrapper = styled(Box)<
	Pick<TanstackTableProps<unknown>, 'background' | 'borderColor' | 'bordered' | 'scrollHeight'>
>`
	overflow-y: auto;
	max-height: ${({ scrollHeight }) => scrollHeight};
	border: ${({ bordered, borderColor, theme: { table } }) =>
		bordered ? (borderColor ? `1px solid ${borderColor}` : table.border) : 'none'};
	border-radius: ${({ theme: { table } }) => table.radius};
	background-color: ${({ background, theme: { table } }) => background || table.background};
	margin-bottom: auto;
`;

export const PaginationWrapper = styled(Box)<{ isLoading: boolean }>`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	padding: 12px 24px;
	pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'auto')};
	opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};

	@media ${({ theme: { media } }) => media.mobile} {
		justify-content: flex-start;
		padding: 12px;
	}
`;

export const PaginationDivider = styled.span`
	color: ${({ theme: { pagination } }) => pagination.color};
`;

export const Table = styled('table')<Pick<TanstackTableProps<unknown>, 'background' | 'width'>>`
	border-collapse: collapse;
	width: ${({ width }) => width};
	font-size: ${({ theme: { table } }) => table.fontSize};
	background-color: ${({ background, theme: { table } }) => background || table.background};
	border-top: none;

	tbody tr:last-child > * {
		border-bottom: none;
	}
`;

export const TableHead = styled.thead<{ zIndex: TanstackTableProps<unknown>['zIndexHeader'] }>`
	position: sticky;
	top: 0;
	z-index: ${({ zIndex }) => zIndex};
`;

export const TableHeader = styled('th')<
	Pick<
		TanstackTableProps<unknown>,
		'cellPadding' | 'align' | 'background' | 'headBackground' | 'headColor' | 'headWhiteSpace'
	>
>`
	padding: ${({ cellPadding }) => cellPadding};
	color: ${({ headColor, theme: { table } }) => headColor || table.headColor};
	background-color: ${({ headBackground, background, theme: { table } }) =>
		headBackground || background || table.headBackground};
	text-align: ${({ align }) => align};
	white-space: ${({ headWhiteSpace }) => headWhiteSpace};

	@media ${({ theme: { media } }) => media.mobile} {
		padding: 8px 22px;
	}
`;

export const TableRow = styled.tr<{
	background?: TanstackTableProps<unknown>['background'];
	backgroundColor?: string;
	clickable?: boolean;
}>`
	position: relative;
	background-color: ${({ background, backgroundColor, theme: { table } }) =>
		backgroundColor || background || table.background};
	cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

export const TableCell = styled('td')<Pick<TanstackTableProps<unknown>, 'borderColor' | 'cellPadding'>>`
	border-bottom: ${({ borderColor, theme: { table } }) =>
		borderColor ? `1px solid ${borderColor}` : table.rowBorder || table.border};
	color: ${({ theme: { table } }) => table.bodyColor};
	padding: ${({ cellPadding }) => cellPadding};

	@media ${({ theme: { media } }) => media.mobile} {
		padding: 8px 22px;
	}
`;

export const GroupTitle = styled(TableCell)<
	Pick<TanstackTableProps<unknown>, 'background' | 'headBackground' | 'headColor'>
>`
	color: ${({ headColor, theme: { table } }) => headColor || table.headColor};
	background: ${({ headBackground, background, theme: { table } }) =>
		headBackground || background || table.headBackground};
`;

export const MultiLineTruncate = styled(Box)<{ lineClamp?: number }>`
	display: -webkit-box;
	-webkit-line-clamp: ${({ lineClamp }) => lineClamp || 2};
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const RowDashedBox = styled.div`
	position: absolute;
	top: 6px;
	bottom: 6px;
	left: 16px;
	right: 40px;
	border: ${({ theme: { box } }) => `1px dashed ${box.border}`};
	border-radius: 6px;
	pointer-events: none;
`;

export const EmptyState = styled(Box)`
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
	padding: 48px;
	color: ${({ theme }) => theme.colors.neutral};
	margin-bottom: auto;
`;
